const PLATFORM_API_BASE = '/api/platform/v1';
const PLATFORM_DEVICE_KEY = 'pontomais.platform.device.v1';
const PLATFORM_CSRF_KEY = 'pontomais.platform.csrf.v1';

export const PLATFORM_SESSION_REPLACED_EVENT = 'pontomais:platform-session-replaced';
export const PLATFORM_SESSION_REPLACED_MESSAGE = 'Este usuário fez login em outro dispositivo.';

let currentSession = null;
let refreshPromise = null;
let replacementNotified = false;

export class PlatformAdminError extends Error {
  constructor(message, { status = 0, code = 'platform_admin_error', details = null } = {}) {
    super(message);
    this.name = 'PlatformAdminError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_error) {
    return null;
  }
}

function randomIdentifier(prefix) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

export function getPlatformDeviceId() {
  const storage = getSessionStorage();
  const saved = storage?.getItem(PLATFORM_DEVICE_KEY);
  if (saved && saved.length >= 8 && saved.length <= 200) return saved;
  const created = randomIdentifier('platform-web');
  storage?.setItem(PLATFORM_DEVICE_KEY, created);
  return created;
}

function actorFromData(data) {
  if (!data || typeof data !== 'object') return null;
  return data.actor || data.platformUser || data.platform_user || data.user || null;
}

function normalizeSession(payload) {
  const data = payload?.data || payload || {};
  const sessionData = data.session && typeof data.session === 'object' ? data.session : {};
  const accessToken = data.accessToken || data.access_token
    || sessionData.accessToken || sessionData.access_token || null;
  const csrfToken = data.csrfToken || data.csrf_token
    || sessionData.csrfToken || sessionData.csrf_token || null;
  if (!accessToken || !csrfToken) return null;
  return {
    accessToken,
    csrfToken,
    actor: actorFromData(data),
    heartbeatIntervalMs: Number(
      data.heartbeatIntervalMs || data.heartbeat_interval_ms
      || sessionData.heartbeatIntervalMs || sessionData.heartbeat_interval_ms || 0
    ) || null
  };
}

function persistSession(session) {
  currentSession = session;
  const storage = getSessionStorage();
  if (!session?.csrfToken) {
    storage?.removeItem(PLATFORM_CSRF_KEY);
    return;
  }
  // O bearer nunca sai da memória. Somente o valor anti-CSRF, inutilizável
  // sem o refresh cookie HttpOnly e o device vinculado, sobrevive ao reload.
  storage?.setItem(PLATFORM_CSRF_KEY, session.csrfToken);
  replacementNotified = false;
}

function errorCode(payload, status) {
  if (typeof payload?.error === 'string') return payload.error;
  return payload?.error?.code || payload?.code || `http_${status}`;
}

function errorMessage(payload, fallback) {
  return payload?.message || payload?.error?.message || payload?.error_description || fallback;
}

async function parseResponse(response) {
  const contentType = String(response.headers?.get?.('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) return null;
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
}

function dispatchSessionReplaced() {
  currentSession = null;
  refreshPromise = null;
  getSessionStorage()?.removeItem(PLATFORM_CSRF_KEY);
  if (replacementNotified || typeof window === 'undefined') return;
  replacementNotified = true;
  window.dispatchEvent(new CustomEvent(PLATFORM_SESSION_REPLACED_EVENT, {
    detail: Object.freeze({ message: PLATFORM_SESSION_REPLACED_MESSAGE })
  }));
}

function responseError(response, payload, fallback) {
  return new PlatformAdminError(errorMessage(payload, fallback), {
    status: response.status,
    code: errorCode(payload, response.status),
    details: payload?.error?.details || payload?.details || null
  });
}

async function fetchResponse(path, options) {
  try {
    const response = await fetch(`${PLATFORM_API_BASE}${path}`, options);
    return { response, payload: await parseResponse(response) };
  } catch (_error) {
    throw new PlatformAdminError(
      'O painel da Help Seller está temporariamente indisponível. Tente novamente em alguns instantes.',
      { code: 'network_unavailable' }
    );
  }
}

async function request(path, {
  method = 'GET',
  body,
  authenticated = false,
  retryAfterRefresh = true,
  idempotencyKey = null
} = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (authenticated && currentSession?.accessToken) {
    headers.Authorization = `Bearer ${currentSession.accessToken}`;
    headers['X-PontoMais-Platform-Device-Id'] = getPlatformDeviceId();
    if (currentSession.csrfToken) headers['X-PontoMais-Platform-CSRF'] = currentSession.csrfToken;
  }
  if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

  const { response, payload } = await fetchResponse(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'include'
  });
  const code = errorCode(payload, response.status);

  if (response.status === 401 && code === 'session_replaced') {
    dispatchSessionReplaced();
    throw responseError(response, payload, PLATFORM_SESSION_REPLACED_MESSAGE);
  }

  if (response.status === 401 && authenticated && retryAfterRefresh) {
    await refreshPlatformAdminSession();
    return request(path, {
      method,
      body,
      authenticated,
      retryAfterRefresh: false,
      idempotencyKey
    });
  }

  if (!response.ok || payload?.ok === false) {
    throw responseError(
      response,
      payload,
      response.status >= 500
        ? 'O painel da Help Seller está temporariamente indisponível.'
        : 'Não foi possível concluir a solicitação.'
    );
  }
  return payload?.data ?? payload;
}

function pathId(value, label) {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    throw new PlatformAdminError(`Informe ${label}.`, { code: `invalid_${label.replace(/\s+/g, '_')}` });
  }
  return encodeURIComponent(normalized);
}

function queryString(filters = {}) {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value).trim());
    }
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

function mutationKey(scope, supplied) {
  return String(supplied || randomIdentifier(`platform-${scope}`)).slice(0, 180);
}

export function clearPlatformAdminSession() {
  currentSession = null;
  refreshPromise = null;
  getSessionStorage()?.removeItem(PLATFORM_CSRF_KEY);
}

export function hasPlatformAdminSession() {
  return Boolean(currentSession?.accessToken);
}

export function hasRecoverablePlatformAdminSession() {
  return hasPlatformAdminSession() || Boolean(getSessionStorage()?.getItem(PLATFORM_CSRF_KEY));
}

export async function loginPlatformAdmin({ email, password, deviceLabel } = {}) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: {
      email: String(email || '').trim().toLowerCase(),
      password,
      deviceId: getPlatformDeviceId(),
      deviceLabel: String(deviceLabel || (typeof navigator === 'undefined' ? 'Navegador' : navigator.userAgent)).slice(0, 120),
      clientKind: 'web'
    }
  });
  const session = normalizeSession(data);
  if (!session) {
    throw new PlatformAdminError('O servidor não devolveu uma sessão administrativa válida.', {
      code: 'invalid_session_response'
    });
  }
  persistSession(session);
  return session.actor || actorFromData(data) || data;
}

async function performRefresh() {
  const csrfToken = currentSession?.csrfToken || getSessionStorage()?.getItem(PLATFORM_CSRF_KEY);
  if (!csrfToken) {
    throw new PlatformAdminError('Não existe sessão administrativa recuperável.', {
      code: 'session_not_recoverable'
    });
  }
  const { response, payload } = await fetchResponse('/auth/refresh', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-PontoMais-Platform-CSRF': csrfToken,
      'X-PontoMais-Platform-Device-Id': getPlatformDeviceId()
    },
    credentials: 'include'
  });
  const code = errorCode(payload, response.status);
  if (response.status === 401 && code === 'session_replaced') {
    dispatchSessionReplaced();
    throw responseError(response, payload, PLATFORM_SESSION_REPLACED_MESSAGE);
  }
  if (!response.ok || payload?.ok === false) {
    clearPlatformAdminSession();
    throw responseError(response, payload, 'Sua sessão administrativa expirou. Entre novamente.');
  }
  const session = normalizeSession(payload);
  if (!session) {
    clearPlatformAdminSession();
    throw new PlatformAdminError('Sua sessão administrativa expirou. Entre novamente.', {
      code: 'invalid_session_response'
    });
  }
  persistSession(session);
  return session.actor || actorFromData(payload?.data || payload);
}

export function refreshPlatformAdminSession() {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export async function restorePlatformAdminSession() {
  if (!hasRecoverablePlatformAdminSession()) return null;
  try {
    if (!hasPlatformAdminSession()) await refreshPlatformAdminSession();
    const actor = await request('/auth/me', { authenticated: true });
    currentSession = { ...currentSession, actor: actorFromData(actor) || actor };
    return currentSession.actor;
  } catch (error) {
    if (error?.code === 'session_replaced') throw error;
    if (error?.status === 401 || ['session_not_recoverable', 'invalid_session_response'].includes(error?.code)) {
      clearPlatformAdminSession();
      return null;
    }
    throw error;
  }
}

export function getPlatformAdminActor() {
  return request('/auth/me', { authenticated: true });
}

export function heartbeatPlatformAdminSession() {
  return request('/auth/heartbeat', { method: 'POST', authenticated: true });
}

export async function logoutPlatformAdmin() {
  try {
    if (!hasPlatformAdminSession() && hasRecoverablePlatformAdminSession()) {
      await refreshPlatformAdminSession();
    }
    if (hasPlatformAdminSession()) {
      await request('/auth/logout', {
        method: 'POST',
        authenticated: true,
        retryAfterRefresh: false
      });
    }
  } finally {
    clearPlatformAdminSession();
  }
}

export function getPlatformAdminHeartbeatInterval() {
  const configured = Number(currentSession?.heartbeatIntervalMs || 0);
  return configured > 0 ? Math.max(10_000, Math.min(30_000, configured)) : 30_000;
}

export function startPlatformAdminHeartbeat({ onError, onSessionReplaced } = {}) {
  if (!hasPlatformAdminSession()) return () => {};
  let stopped = false;
  let timer = null;
  const schedule = () => {
    if (!stopped && hasPlatformAdminSession()) {
      timer = setTimeout(run, getPlatformAdminHeartbeatInterval());
    }
  };
  const run = async () => {
    try {
      await heartbeatPlatformAdminSession();
    } catch (error) {
      if (typeof onError === 'function') onError(error);
      if (error?.code === 'session_replaced') {
        stopped = true;
        if (typeof onSessionReplaced === 'function') onSessionReplaced(error);
        return;
      }
      if (!hasPlatformAdminSession()) {
        stopped = true;
        return;
      }
    }
    schedule();
  };
  schedule();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

// Alias explícito para a integração do App, mantendo a nomenclatura das demais
// sessões centrais do produto.
export const startPlatformAdminSessionHeartbeat = startPlatformAdminHeartbeat;

export function getPlatformOverview() {
  return request('/overview', { authenticated: true });
}

export function lookupPlatformCnpj(value) {
  const cnpj = String(value || '').replace(/\D/g, '');
  return request(`/lookups/cnpj/${encodeURIComponent(cnpj)}`, { authenticated: true });
}

export function lookupPlatformCep(value) {
  const cep = String(value || '').replace(/\D/g, '');
  return request(`/lookups/cep/${encodeURIComponent(cep)}`, { authenticated: true });
}

export function listPlatformAffiliates(filters = {}) {
  return request(`/affiliates${queryString(filters)}`, { authenticated: true });
}

export function listPlatformPartnerApplications() {
  return request('/partner-applications', { authenticated: true });
}

export function reviewPlatformPartnerApplication(applicationId, input, options = {}) {
  return request(`/partner-applications/${pathId(applicationId, 'application')}`, {
    method: 'PATCH',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('partner-application-review', options.idempotencyKey)
  });
}

export function getPlatformAffiliate(affiliateId) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}`, { authenticated: true });
}

export function createPlatformAffiliate(input, options = {}) {
  return request('/affiliates', {
    method: 'POST',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-create', options.idempotencyKey)
  });
}

export function updatePlatformAffiliate(affiliateId, input, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}`, {
    method: 'PUT',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-update', options.idempotencyKey)
  });
}

export function getPlatformAffiliateCampaign(affiliateId) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/campaign`, { authenticated: true });
}

export function updatePlatformAffiliateCampaign(affiliateId, input, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/campaign`, {
    method: 'PUT',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-campaign', options.idempotencyKey)
  });
}

export function resetPlatformAffiliatePassword(affiliateId, password, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/reset-password`, {
    method: 'POST',
    body: { password },
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-password', options.idempotencyKey)
  });
}

export function assignPlatformAffiliateTenant(affiliateId, input, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/referrals`, {
    method: 'POST',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-referral', options.idempotencyKey)
  });
}

export function updatePlatformAffiliateReferral(affiliateId, referralId, status, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/referrals/${pathId(referralId, 'referral')}`, {
    method: 'PATCH',
    body: { status },
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-referral-update', options.idempotencyKey)
  });
}

export function createPlatformAffiliateCommission(affiliateId, input, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/commissions`, {
    method: 'POST',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-commission', options.idempotencyKey)
  });
}

export function updatePlatformAffiliateCommission(affiliateId, commissionId, action, options = {}) {
  return request(`/affiliates/${pathId(affiliateId, 'affiliate')}/commissions/${pathId(commissionId, 'commission')}`, {
    method: 'PATCH',
    body: { action },
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-commission-update', options.idempotencyKey)
  });
}

export function listPlatformAffiliateWithdrawals(filters = {}) {
  return request(`/affiliates/withdrawals${queryString(filters)}`, { authenticated: true });
}

export function reviewPlatformAffiliateWithdrawal(withdrawalId, input, options = {}) {
  return request(`/affiliates/withdrawals/${pathId(withdrawalId, 'withdrawal')}`, {
    method: 'PATCH',
    body: input,
    authenticated: true,
    idempotencyKey: mutationKey('affiliate-withdrawal-review', options.idempotencyKey)
  });
}

export function listPlatformTenants(filters = {}) {
  return request(`/tenants${queryString(filters)}`, { authenticated: true });
}

export function updatePlatformTenant(tenantId, input, options = {}) {
  if (input?.status !== undefined && input?.planCode !== undefined) {
    return Promise.all([
      updatePlatformTenantStatus(tenantId, input.status, options),
      updatePlatformTenantPlan(tenantId, input.planCode, options)
    ]);
  }
  if (input?.status !== undefined) return updatePlatformTenantStatus(tenantId, input.status, options);
  if (input?.planCode !== undefined) return updatePlatformTenantPlan(tenantId, input.planCode, options);
  throw new PlatformAdminError('Informe o status ou o plano que deseja alterar.', {
    code: 'empty_tenant_update'
  });
}

export function updatePlatformTenantStatus(tenantId, status, options = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/status`, {
    method: 'PATCH',
    body: { status },
    authenticated: true,
    idempotencyKey: mutationKey('tenant-status', options.idempotencyKey)
  });
}

export function updatePlatformTenantPlan(tenantId, planCode, options = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/plan`, {
    method: 'PATCH',
    body: { planCode },
    authenticated: true,
    idempotencyKey: mutationKey('tenant-plan', options.idempotencyKey)
  });
}

export function updatePlatformTenantLicense(tenantId, input, options = {}) {
  const body = {
    status: input?.status,
    extraSeats: input?.extraSeats,
    persistenceMode: input?.persistenceMode,
    billingReference: input?.billingReference
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return request(`/tenants/${pathId(tenantId, 'tenant')}/license`, {
    method: 'PATCH',
    body,
    authenticated: true,
    idempotencyKey: mutationKey('license', options.idempotencyKey)
  });
}

export function listPlatformTenantUsers(tenantId, filters = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/users${queryString(filters)}`, {
    authenticated: true
  });
}

export function createPlatformTenantUser(tenantId, input, options = {}) {
  const body = {
    username: input?.username,
    displayName: input?.displayName,
    role: input?.role,
    password: input?.password
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return request(`/tenants/${pathId(tenantId, 'tenant')}/users`, {
    method: 'POST',
    body,
    authenticated: true,
    idempotencyKey: mutationKey('tenant-user-create', options.idempotencyKey)
  });
}

export function updatePlatformTenantUser(tenantId, userId, input, options = {}) {
  const body = {
    displayName: input?.displayName,
    role: input?.role,
    status: input?.status
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return request(`/tenants/${pathId(tenantId, 'tenant')}/users/${pathId(userId, 'usuario')}`, {
    method: 'PATCH',
    body,
    authenticated: true,
    idempotencyKey: mutationKey('tenant-user-update', options.idempotencyKey)
  });
}

export function resetPlatformTenantUserPassword(tenantId, userId, password, options = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/users/${pathId(userId, 'usuario')}/reset-password`, {
    method: 'POST',
    body: { password },
    authenticated: true,
    idempotencyKey: mutationKey('tenant-user-password', options.idempotencyKey)
  });
}

export function getPlatformTenantModules(tenantId) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/modules`, {
    authenticated: true
  });
}

export function updatePlatformTenantModules(tenantId, input, options = {}) {
  const body = {
    expectedVersion: input?.expectedVersion,
    modules: Array.isArray(input?.modules)
      ? input.modules.map((module) => ({ key: module?.key ?? module?.code, enabled: Boolean(module?.enabled) }))
      : [],
    reason: input?.reason
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return request(`/tenants/${pathId(tenantId, 'tenant')}/modules`, {
    method: 'PUT',
    body,
    authenticated: true,
    idempotencyKey: mutationKey('tenant-modules', options.idempotencyKey)
  });
}

function platformBannerPayload(input = {}) {
  const payload = {
    titulo: input?.titulo,
    descricao: input?.descricao,
    tipo: input?.tipo,
    parceiro: input?.parceiro,
    imagem_url: input?.imagem_url,
    imagem_base64: input?.imagem_base64,
    cta_texto: input?.cta_texto,
    link: input?.link,
    prioridade: input?.prioridade,
    abrir_nova_aba: input?.abrir_nova_aba,
    ativo: input?.ativo,
    data_inicio: input?.data_inicio,
    data_fim: input?.data_fim
  };
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  return payload;
}

export function listPlatformGlobalBanners() {
  return request('/banners', { authenticated: true });
}

export function getPlatformGlobalBanner(bannerId) {
  return request(`/banners/${pathId(bannerId, 'banner')}`, { authenticated: true });
}

export function createPlatformGlobalBanner(input, options = {}) {
  return request('/banners', {
    method: 'POST',
    body: { payload: platformBannerPayload(input) },
    authenticated: true,
    idempotencyKey: mutationKey('global-banner-create', options.idempotencyKey)
  });
}

export function updatePlatformGlobalBanner(bannerId, expectedVersion, input, options = {}) {
  return request(`/banners/${pathId(bannerId, 'banner')}`, {
    method: 'PUT',
    body: { expectedVersion: String(expectedVersion ?? ''), payload: platformBannerPayload(input) },
    authenticated: true,
    idempotencyKey: mutationKey('global-banner-update', options.idempotencyKey)
  });
}

export function deletePlatformGlobalBanner(bannerId, expectedVersion, options = {}) {
  return request(`/banners/${pathId(bannerId, 'banner')}`, {
    method: 'DELETE',
    body: { expectedVersion: String(expectedVersion ?? '') },
    authenticated: true,
    idempotencyKey: mutationKey('global-banner-delete', options.idempotencyKey)
  });
}

export function listPlatformTenantBanners(tenantId) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/banners`, {
    authenticated: true
  });
}

export function getPlatformTenantBanner(tenantId, bannerId) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/banners/${pathId(bannerId, 'banner')}`, {
    authenticated: true
  });
}

export function createPlatformTenantBanner(tenantId, input, options = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/banners`, {
    method: 'POST',
    body: { payload: platformBannerPayload(input) },
    authenticated: true,
    idempotencyKey: mutationKey('tenant-banner-create', options.idempotencyKey)
  });
}

export function updatePlatformTenantBanner(tenantId, bannerId, expectedVersion, input, options = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/banners/${pathId(bannerId, 'banner')}`, {
    method: 'PUT',
    body: { expectedVersion: String(expectedVersion ?? ''), payload: platformBannerPayload(input) },
    authenticated: true,
    idempotencyKey: mutationKey('tenant-banner-update', options.idempotencyKey)
  });
}

export function deletePlatformTenantBanner(tenantId, bannerId, expectedVersion, options = {}) {
  return request(`/tenants/${pathId(tenantId, 'tenant')}/banners/${pathId(bannerId, 'banner')}`, {
    method: 'DELETE',
    body: { expectedVersion: String(expectedVersion ?? '') },
    authenticated: true,
    idempotencyKey: mutationKey('tenant-banner-delete', options.idempotencyKey)
  });
}

export function listPlatformSubscriptions(filters = {}) {
  return request(`/subscriptions${queryString(filters)}`, { authenticated: true });
}

export function updatePlatformSubscription(tenantId, input, options = {}) {
  const body = {
    planCode: input?.planCode,
    billingCycle: input?.billingCycle,
    priceCents: input?.priceCents,
    status: input?.status,
    provider: input?.provider,
    providerSubscriptionId: input?.providerSubscriptionId,
    startsAt: input?.startsAt || null,
    currentPeriodEndsAt: input?.currentPeriodEndsAt || null,
    notes: input?.notes
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return request(`/subscriptions/${pathId(tenantId, 'tenant')}`, {
    method: 'PUT',
    body,
    authenticated: true,
    idempotencyKey: mutationKey('subscription', options.idempotencyKey)
  });
}

export function listPlatformTickets(filters = {}) {
  return request(`/tickets${queryString(filters)}`, { authenticated: true });
}

export function getPlatformTicket(ticketId) {
  return request(`/tickets/${pathId(ticketId, 'ticket')}`, { authenticated: true });
}

export function updatePlatformTicket(ticketId, input, options = {}) {
  const body = {
    status: input?.status,
    priority: input?.priority,
    category: input?.category,
    assignedPlatformUserId: input?.assignedPlatformUserId
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return request(`/tickets/${pathId(ticketId, 'ticket')}`, {
    method: 'PATCH',
    body,
    authenticated: true,
    idempotencyKey: mutationKey('ticket', options.idempotencyKey)
  });
}

export function replyPlatformTicket(ticketId, message, options = {}) {
  return request(`/tickets/${pathId(ticketId, 'ticket')}/messages`, {
    method: 'POST',
    body: { message: String(message || '').trim(), isInternal: Boolean(options.isInternal) },
    authenticated: true,
    idempotencyKey: mutationKey('ticket-message', options.idempotencyKey)
  });
}

export const __platformAdminTestUtils = {
  PLATFORM_API_BASE,
  PLATFORM_DEVICE_KEY,
  PLATFORM_CSRF_KEY,
  normalizeSession,
  errorCode,
  dropMemorySession() {
    currentSession = null;
    refreshPromise = null;
  },
  reset() {
    currentSession = null;
    refreshPromise = null;
    replacementNotified = false;
    const storage = getSessionStorage();
    storage?.removeItem(PLATFORM_DEVICE_KEY);
    storage?.removeItem(PLATFORM_CSRF_KEY);
  }
};
